import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button } from "@impero-com/ui";
import { Drawer } from "antd";
import { UseFieldArrayAppend, UseFormTrigger } from "react-hook-form";

import { EntityListLinkedResources, EntityOverview } from "api/generated/model";
import {
    CONTROL_ENTITIES_KEY,
    ControlProgramFormSchema,
} from "org/control/controlProgram/view/useControlProgramViewFormSchema";

import * as theme from "./ControlEntitiesTables.css";
import { SelectEntitiesTable } from "./SelectEntitiesTable";

interface SelectedRows {
    [key: string]: boolean;
}
interface EntityId {
    entityId: number;
}

interface AddEntitiesTableProps {
    linkedResources: EntityListLinkedResources;
    open: boolean;
    onClose: () => void;
    controlEntities: EntityOverview[];
    append: UseFieldArrayAppend<
        ControlProgramFormSchema,
        typeof CONTROL_ENTITIES_KEY
    >;
    trigger: UseFormTrigger<ControlProgramFormSchema>;
}

export function AddEntitiesTable({
    linkedResources,
    open,
    onClose,
    controlEntities,
    append,
    trigger,
}: AddEntitiesTableProps) {
    const { t } = useTranslation();

    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const selectedEntityIds = Object.keys(selectedRows);
    const closeAndClearSelect = () => {
        setSelectedRows({});
        onClose();
    };

    const handleSelectEntities = () => {
        const entityIdsToAdd: EntityId[] = controlEntities
            .filter((entity) =>
                selectedEntityIds.includes(entity.id.toString())
            )
            .map((entity) => ({
                entityId: entity.id,
            }));

        append(entityIdsToAdd);
        trigger(CONTROL_ENTITIES_KEY);
        closeAndClearSelect();
    };

    const buttons = (
        <div className={theme.drawerActionsContainer}>
            <Button
                disabled={selectedEntityIds.length === 0}
                size="large"
                variant="primary"
                onClick={handleSelectEntities}
            >
                {t("EditControlProgram_DrawerAction_AddEntities")}
            </Button>

            <Button
                size="large"
                variant="default"
                onClick={closeAndClearSelect}
            >
                {t("EditControlProgram_DrawerAction_Cancel")}
            </Button>
        </div>
    );

    return (
        <Drawer
            title={t("EditControlProgram_DrawerTitle")}
            placement="bottom"
            open={open}
            onClose={closeAndClearSelect}
            size="large"
            closeIcon={null}
            extra={buttons}
            bodyStyle={{ padding: 0 }}
        >
            <SelectEntitiesTable
                linkedResources={linkedResources}
                controlEntities={controlEntities}
                selectedRows={selectedRows}
                onSelectedRowsChange={setSelectedRows}
            />
        </Drawer>
    );
}
